import type { ProviderId, SyncChange, SyncCursor } from '@domain/sync/changeModel';
import { MANAGED_ROW_STORE, validateChange } from '@domain/sync/changeModel';
import {
  getSupabaseSyncClient,
  type SupabaseLikeSession,
  type SupabaseSyncClient,
} from '@platform/auth/supabaseAuth';
import {
  ProviderError,
  type ProviderStatus,
  type SkippedChange,
  type SyncProvider,
} from './types';

interface RpcError {
  readonly message?: string;
  readonly code?: string;
}

interface PullResponse {
  readonly changes: unknown;
  readonly next_cursor: string | null;
  readonly requires_snapshot: boolean;
}

interface SupabaseSyncTransport {
  readonly auth: {
    getSession: () => Promise<{
      readonly data: { readonly session?: SupabaseLikeSession | null };
      readonly error: RpcError | null;
    }>;
  };
  readonly rpc: SupabaseSyncClient['rpc'];
}

export interface RemoteDevice {
  readonly deviceId: string;
  readonly label: string;
  readonly platform: string;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly revokedAt: string | null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  const value = record(error);
  return typeof value?.message === 'string' ? value.message : 'Sync request failed.';
}

function toProviderError(error: unknown): ProviderError {
  if (error instanceof ProviderError) return error;
  const value = record(error) as RpcError | null;
  const code = value?.code ?? '';
  const message = errorMessage(error);
  if (/jwt|expired|no active session|not authenticated|not signed in/i.test(message)) {
    return new ProviderError('NOT_AUTHENTICATED', message, false, { cause: error });
  }
  if (code === '42501' || code === 'PGRST301' || /permission|not permitted|not authorized|owned by another/i.test(message)) {
    return new ProviderError('FORBIDDEN', message, false, { cause: error });
  }
  if (/network|fetch|offline|timeout|temporar/i.test(message)) {
    return new ProviderError('UNREACHABLE', message, true, { cause: error });
  }
  if (code === '23505' || code === '22023' || code === 'P0001') {
    return new ProviderError('REJECTED', message, false, { cause: error });
  }
  return new ProviderError('UNKNOWN', message, true, { cause: error });
}

interface ParsedPage {
  readonly changes: SyncChange[];
  readonly skipped: SkippedChange[];
}

/**
 * Split one stored page into what this client can use and what it cannot.
 *
 * WHY A BAD ROW IS SKIPPED AND A BAD ENVELOPE IS NOT
 * --------------------------------------------------
 * `input` is the provider's own contract: not an array means the server is not
 * speaking the protocol, and pretending it sent nothing would look like a clean
 * pull. A row inside the array is different — it is DATA, possibly written by an
 * older client or a migration, and refusing the whole page for it makes every
 * later change undeliverable, forever, because the cursor cannot move past it.
 * Such a row is named and left in the cloud, and the caller decides.
 *
 * THE ONE ROW THAT STILL THROWS
 * -----------------------------
 * A change naming another business is not malformed data, it is the isolation
 * gate failing. Skipping it quietly would make a server that hands out another
 * account's rows look normal, so it still refuses the request outright.
 */
function parsePage(input: unknown, businessId: string): ParsedPage {
  if (!Array.isArray(input)) {
    throw new ProviderError('REJECTED', 'The sync server returned an invalid change batch.', false);
  }

  const changes: SyncChange[] = [];
  const skipped: SkippedChange[] = [];
  for (const raw of input) {
    const validation = validateChange(raw, businessId);
    if (validation.ok) {
      changes.push(validation.change);
      continue;
    }
    if (validation.reason === 'BUSINESS_MISMATCH') {
      throw new ProviderError(
        'REJECTED',
        `The sync server returned an invalid change: ${validation.reason}.`,
        false,
      );
    }
    const changeId = (raw as { readonly changeId?: unknown })?.changeId;
    skipped.push({
      changeId: typeof changeId === 'string' ? changeId : null,
      reason: validation.reason,
      detail: validation.detail,
    });
  }

  if (skipped.length > 0) {
    // The engine carries the same facts into the status the user sees. This line
    // exists for the console we cannot otherwise inspect on a customer's machine.
    console.warn(
      `[sync] Skipped ${skipped.length} unreadable change(s) from the cloud for ${businessId}.`,
      skipped.map((s) => `${s.changeId ?? 'unknown'}: ${s.reason}`),
    );
  }

  return { changes, skipped };
}

function parseDevice(value: unknown): RemoteDevice {
  const row = record(value);
  if (
    !row ||
    typeof row.device_id !== 'string' ||
    typeof row.label !== 'string' ||
    typeof row.platform !== 'string' ||
    typeof row.first_seen_at !== 'string' ||
    typeof row.last_seen_at !== 'string' ||
    (row.revoked_at !== null && typeof row.revoked_at !== 'string')
  ) {
    throw new ProviderError('REJECTED', 'The sync server returned an invalid device record.', false);
  }
  return {
    deviceId: row.device_id,
    label: row.label,
    platform: row.platform,
    firstSeenAt: row.first_seen_at,
    lastSeenAt: row.last_seen_at,
    revokedAt: row.revoked_at,
  };
}

export class SupabaseSyncProvider implements SyncProvider {
  readonly id: ProviderId = 'supabase';
  readonly label = 'DNR Cloud Sync';

  constructor(private readonly getClient: () => SupabaseSyncTransport = getSupabaseSyncClient) {}

  capabilities() {
    // Polling only: SyncCoordinator drives runs on launch, online, focus and a
    // 30s timer. No Realtime subscription is wired, so realtime stays false and
    // the engine must not assume the server pushes changes to us.
    return { ...MANAGED_ROW_STORE, realtime: false };
  }

  private async rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
    try {
      const { data, error } = await this.getClient().rpc(name, args);
      if (error) throw error;
      return data as T;
    } catch (error) {
      throw toProviderError(error);
    }
  }

  async getStatus(): Promise<ProviderStatus> {
    try {
      const client = this.getClient();
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      const session = data.session ?? null;
      if (!session) {
        return {
          state: 'unauthenticated',
          authorisedBusinessIds: [],
          message: 'Sign in to your DNR account to sync.',
          lastCheckedAt: new Date().toISOString(),
        };
      }
      const businessIds = await this.rpc<unknown>('sync_list_businesses', {});
      if (!Array.isArray(businessIds) || businessIds.some((id) => typeof id !== 'string')) {
        throw new ProviderError('REJECTED', 'The sync server returned an invalid business list.', false);
      }
      return {
        state: 'connected',
        accountLabel: session.user.email ?? session.user.id,
        authorisedBusinessIds: businessIds,
        lastCheckedAt: new Date().toISOString(),
      };
    } catch (error) {
      const mapped = toProviderError(error);
      return {
        state: mapped.code === 'NOT_AUTHENTICATED' ? 'unauthenticated' : 'error',
        authorisedBusinessIds: [],
        message: mapped.message,
        lastCheckedAt: new Date().toISOString(),
      };
    }
  }

  async isReachable(): Promise<boolean> {
    return (await this.getStatus()).state === 'connected';
  }

  async claimBusiness(businessId: string): Promise<void> {
    await this.rpc('sync_claim_business', { p_business_id: businessId });
  }

  async registerDevice(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly label: string;
    readonly platform: string;
  }): Promise<void> {
    await this.rpc('sync_register_device', {
      p_business_id: input.businessId,
      p_device_id: input.deviceId,
      p_label: input.label,
      p_platform: input.platform,
    });
  }

  async listDevices(businessId: string): Promise<RemoteDevice[]> {
    const rows = await this.rpc<unknown>('sync_list_devices', { p_business_id: businessId });
    if (!Array.isArray(rows)) {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid device list.', false);
    }
    return rows.map(parseDevice);
  }

  async revokeDevice(businessId: string, deviceId: string): Promise<void> {
    await this.rpc('sync_revoke_device', {
      p_business_id: businessId,
      p_device_id: deviceId,
    });
  }

  async pushChanges(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly changes: readonly SyncChange[];
  }): Promise<{ readonly accepted: readonly string[] }> {
    const result = await this.rpc<unknown>('sync_push_changes', {
      p_business_id: input.businessId,
      p_device_id: input.deviceId,
      p_changes: input.changes,
    });
    if (
      !Array.isArray(result) ||
      result.some((id) => typeof id !== 'string' || !input.changes.some((change) => change.changeId === id))
    ) {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid push acknowledgement.', false);
    }
    return { accepted: result };
  }

  async pullChanges(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly cursor: SyncCursor | null;
    readonly limit: number;
  }): Promise<{
    readonly changes: readonly SyncChange[];
    readonly nextCursor: SyncCursor | null;
    readonly requiresSnapshot: boolean;
    readonly skipped: readonly SkippedChange[];
  }> {
    const response = await this.rpc<PullResponse>('sync_pull_changes', {
      p_business_id: input.businessId,
      p_device_id: input.deviceId,
      p_cursor: input.cursor?.token ?? '0',
      p_limit: input.limit,
    });
    if (
      !record(response) ||
      (response.next_cursor !== null && typeof response.next_cursor !== 'string') ||
      typeof response.requires_snapshot !== 'boolean'
    ) {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid pull response.', false);
    }
    const page = parsePage(response.changes, input.businessId);
    return {
      changes: page.changes,
      nextCursor: response.next_cursor ? { token: response.next_cursor } : null,
      requiresSnapshot: response.requires_snapshot,
      skipped: page.skipped,
    };
  }

  async fetchSnapshot(input: {
    readonly businessId: string;
  }): Promise<{
    readonly changes: readonly SyncChange[];
    readonly cursor: SyncCursor;
    readonly skipped: readonly SkippedChange[];
  }> {
    const response = await this.rpc<unknown>('sync_fetch_snapshot', {
      p_business_id: input.businessId,
    });
    const row = record(response);
    if (!row || typeof row.cursor !== 'string') {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid snapshot.', false);
    }
    // Same rules as the delta: unreadable rows are named, not thrown, so a
    // legacy record cannot make recovery impossible.
    const page = parsePage(row.changes, input.businessId);
    return {
      changes: page.changes,
      cursor: { token: row.cursor },
      skipped: page.skipped,
    };
  }

  async acknowledge(input: {
    readonly businessId: string;
    readonly deviceId: string;
    readonly cursor: SyncCursor;
  }): Promise<void> {
    await this.rpc('sync_acknowledge', {
      p_business_id: input.businessId,
      p_device_id: input.deviceId,
      p_cursor: input.cursor.token,
    });
  }

  async oldestNeededCursor(input: {
    readonly businessId: string;
  }): Promise<SyncCursor | null> {
    const value = await this.rpc<unknown>('sync_oldest_needed_cursor', {
      p_business_id: input.businessId,
    });
    if (value === null) return null;
    if (typeof value !== 'string') {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid retention cursor.', false);
    }
    return { token: value };
  }

  async checkBusinessRowExists(input: {
    readonly businessId: string;
  }): Promise<{ exists: boolean; totalChanges: number; cursor: string } | null> {
    const result = await this.rpc<unknown>('sync_business_row_exists', {
      p_business_id: input.businessId,
    });
    const row = record(result);
    if (!row) {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid business-row check.', false);
    }
    if (typeof row.business_exists !== 'boolean') {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid business_exists value.', false);
    }
    if (typeof row.total_changes !== 'number' || !Number.isInteger(row.total_changes)) {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid total_changes value.', false);
    }
    if (typeof row.cursor !== 'string') {
      throw new ProviderError('REJECTED', 'The sync server returned an invalid cursor value.', false);
    }
    return {
      exists: row.business_exists,
      totalChanges: row.total_changes,
      cursor: row.cursor,
    };
  }
}

export const supabaseSyncProvider = new SupabaseSyncProvider();
